import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Star, MapPin, Heart, ArrowRight, Wifi, Car, Waves } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import VerifiedBadge from "@/components/VerifiedBadge";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { getFirstImageUrl } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { getBeraOpportunitySortedProperties } from "@/lib/beraOpportunity";
import { useMemo } from "react";

// Áreas prioritárias próximas aos aeroportos de Angola
const AIRPORT_PRIORITY_AREAS = [
  // Aeroporto 4 de Fevereiro (Luanda centro)
  "luanda", "ingombota", "maianga", "samba", "marcal", "mutamba",
  // Novo Aeroporto Agostinho Neto
  "kikuxi", "cidade do kilamba", "kilamba", "camama", "zango", "viana",
  // Outras zonas de alta demanda
  "talatona", "belas", "mussulo", "cabo ledo", "palanca"
];

// Pontuação de proximidade ao aeroporto
const calculateAirportProximityScore = (city: string, province: string): number => {
  const location = `${city} ${province}`.toLowerCase();
  
  // Prioridade máxima para Luanda
  if (location.includes("luanda")) return 100;
  
  // Zonas próximas aos aeroportos
  if (AIRPORT_PRIORITY_AREAS.some(area => location.includes(area))) return 80;
  
  // Outras províncias com aeroportos
  if (["benguela", "lobito", "lubango", "huambo", "cabinda"].some(p => location.includes(p))) return 60;
  
  return 40; // Base score
};

const FeaturedProperties = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  const { data: rawProperties = [], isLoading } = useQuery({
    queryKey: ["featured-properties"],
    queryFn: async () => {
      // Buscar mais propriedades para ter pool de seleção
      const { data, error } = await supabase
        .from("properties")
        .select("id, title, city, province, price_per_night, average_rating, total_reviews, images, amenities, is_verified, created_at, owner_id, property_type, description")
        .eq("status", "active")
        .eq("is_verified", true)
        .limit(20);
      
      if (error) throw error;
      return data || [];
    },
  });

  // Aplicar algoritmo de ordenação inteligente
  const properties = useMemo(() => {
    if (!rawProperties.length) return [];

    // Adicionar score de proximidade ao aeroporto
    const propertiesWithScores = rawProperties.map(p => ({
      ...p,
      airportScore: calculateAirportProximityScore(p.city, p.province),
    }));

    // Separar propriedades por proximidade
    const luandaProperties = propertiesWithScores.filter(p => p.airportScore >= 80);
    const otherProperties = propertiesWithScores.filter(p => p.airportScore < 80);

    // Aplicar BeraOpportunity em cada grupo
    const config = {
      qualityWeight: 0.35,
      popularityWeight: 0.20,
      freshnessWeight: 0.25,
      exposurePenaltyWeight: 0.20,
      maxPropertiesPerOwner: 2,
      rotationEnabled: true,
      boostNewProperties: true,
    };

    const sortedLuanda = getBeraOpportunitySortedProperties(luandaProperties, config);
    const sortedOthers = getBeraOpportunitySortedProperties(otherProperties, config);

    // Estratégia de seleção diversificada:
    // 3 de Luanda (próximo aos aeroportos) + 1 de outra zona
    const selection = [
      ...sortedLuanda.slice(0, 3),
      ...sortedOthers.slice(0, 1),
    ];

    // Se não tivermos 4 propriedades, preencher com o restante
    if (selection.length < 4) {
      const remaining = [...sortedLuanda.slice(3), ...sortedOthers.slice(1)];
      selection.push(...remaining.slice(0, 4 - selection.length));
    }

    return selection.slice(0, 4);
  }, [rawProperties]);

  const getAmenityIcon = (amenity: string) => {
    switch (amenity) {
      case "wifi": return <Wifi className="w-3.5 h-3.5" strokeWidth={1.5} />;
      case "estacionamento":
      case "parking": return <Car className="w-3.5 h-3.5" strokeWidth={1.5} />;
      case "piscina":
      case "pool": return <Waves className="w-3.5 h-3.5" strokeWidth={1.5} />;
      default: return null;
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("pt-AO").format(price) + " Kz";
  };

  const getFirstImage = (images: any) => {
    // Parse images if they come as JSON string from Supabase
    let parsedImages = images;
    if (typeof images === "string") {
      try {
        parsedImages = JSON.parse(images);
      } catch {
        parsedImages = null;
      }
    }

    if (Array.isArray(parsedImages)) {
      const u = parsedImages.find((x: any) => typeof x === "string" && x.trim().length > 0);
      if (typeof u === "string") return u.trim();
    }
    return getFirstImageUrl(null);
  };

  const getAmenities = (amenities: any): string[] => {
    if (Array.isArray(amenities)) return amenities.slice(0, 3);
    return [];
  };

  return (
    <section className="py-8 md:py-10">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-5">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-1.5">
              {t('featuredProperties.title')} <span className="text-gradient">{t('featuredProperties.titleHighlight')}</span>
            </h2>
            <h3 className="text-muted-foreground text-sm font-medium">
              {t('featuredProperties.subtitle')}
            </h3>
          </div>
          <Button variant="ghost" className="group self-start md:self-auto text-sm" asChild>
            <Link to={user ? "/explorar" : "/auth"}>
              {t('featuredProperties.viewAll')}
            </Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="overflow-hidden border-0 shadow-card">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <CardContent className="p-3.5 space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-20" />
                  </CardContent>
                </Card>
              ))
            : properties.map((property) => (
                <Link to={user ? `/propriedade/${property.id}` : "/auth"} key={property.id}>
                  <Card className="group overflow-hidden border-0 shadow-card hover:shadow-elevated transition-all duration-300">
                    <div className="relative aspect-[4/3]">
                      <div className="absolute inset-0 overflow-hidden rounded-t-xl">
                        <OptimizedImage
                          src={getFirstImage(property.images)}
                          alt={property.title}
                          aspectRatio="4/3"
                          objectFit="cover"
                          priority={true}
                          className="group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="absolute top-2.5 left-2.5 z-10 flex flex-wrap gap-1.5">
                        {property.is_verified && <VerifiedBadge size="xs" />}
                      </div>
                      <button
                        className="absolute top-2.5 right-2.5 z-10 w-8 h-8 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors shadow-sm"
                        onClick={(e) => e.preventDefault()}
                      >
                        <Heart className="w-4 h-4 text-muted-foreground hover:text-destructive transition-colors" strokeWidth={1.5} />
                      </button>
                    </div>

                    <CardContent className="p-3.5">
                      <div className="flex items-center gap-1 text-muted-foreground text-xs mb-1">
                        <MapPin className="w-3 h-3" strokeWidth={1.5} />
                        <span>{property.city}, {property.province}</span>
                      </div>
                      <h3 className="font-semibold text-foreground text-sm mb-2 line-clamp-1 group-hover:text-primary transition-colors">
                        {property.title}
                      </h3>
                      <div className="flex items-center gap-2.5 mb-3">
                        {getAmenities(property.amenities).map((amenity) => {
                          const icon = getAmenityIcon(amenity);
                          return icon ? (
                            <div key={amenity} className="text-muted-foreground/70">{icon}</div>
                          ) : null;
                        })}
                      </div>
                      <div className="flex items-center justify-between pt-2.5 border-t border-border/50">
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 fill-zinc-900 text-zinc-900 dark:fill-zinc-100 dark:text-zinc-100" strokeWidth={1.5} />
                          <span className="font-semibold text-sm">{Number(property.average_rating || 0).toFixed(1)}</span>
                          <span className="text-muted-foreground text-xs">({property.total_reviews || 0})</span>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-sm text-zinc-900 dark:text-zinc-100">{formatPrice(property.price_per_night)}</span>
                          <span className="text-muted-foreground text-xs"> {t('featuredProperties.perNight')}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProperties;
